import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AppShell from './components/layout/AppShell';
import Landing from './pages/Landing';
import Login from './pages/auth/Login';
import AcceptInvite from './pages/auth/AcceptInvite';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import About from './pages/About';
import Privacy from './pages/legal/Privacy';
import Terms from './pages/legal/Terms';
import Support from './pages/legal/Support';
import Dashboard from './pages/dashboard/Dashboard';
import CoverageMap from './pages/coverage/CoverageMap';
import Settings from './pages/settings/Settings';
import TaskList from './pages/tasks/TaskList';
import UserList from './pages/users/UserList';
import UserRecord from './pages/users/UserRecord';
import CandidateList from './pages/hiring/CandidateList';
import CandidateDetail from './pages/hiring/CandidateDetail';
import HiringInsights from './pages/hiring/HiringInsights';
import ScheduleWeekPage from './pages/schedule/ScheduleWeekPage';
import ScheduleTimeline from './pages/schedule/ScheduleTimeline';
import UsualWeek from './pages/schedule/UsualWeek';
import WorkAreas from './pages/schedule/WorkAreas';
import PayrollWeek from './pages/payroll/PayrollWeek';
import PayrollHistory from './pages/payroll/PayrollHistory';
import DriverPay from './pages/payroll/DriverPay';
import PayrollReports from './pages/payroll/PayrollReports';
import WeeklyVolume from './pages/payroll/WeeklyVolume';
import { ReportsIndex } from './pages/reports/ReportsPanel';
import CompanyList from './pages/companies/CompanyList';
import Operations from './pages/workflows/Operations';
import FleetList from './pages/fleet/FleetList';
import VehicleRecord from './pages/fleet/VehicleRecord';
import ComplianceBoardPage from './pages/files/ComplianceBoard';
import RosterPage from './pages/files/RosterPage';
import EntityFilePage from './pages/files/EntityFilePage';
import DocumentListPage from './pages/files/DocumentListPage';
import DocumentDetailPage from './pages/files/DocumentDetailPage';
import FolderPage from './pages/files/FolderPage';
import RecentlyDeletedPage from './pages/files/RecentlyDeletedPage';
import OperationDetail from './pages/workflows/OperationDetail';
import BugReports from './pages/settings/BugReports';
import AccessSettings from './pages/settings/AccessSettings';
import { useAuthStore } from './store/authStore';

function App() {
  const { initializeAuth } = useAuthStore();

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Public and unauthenticated on purpose: an app reviewer, and a
          driver deciding whether to accept an invitation, both have to be
          able to read these without an account. The store submission needs
          the privacy and support URLs to resolve for a signed-out visitor. */}
      <Route path="/about" element={<About />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/support" element={<Support />} />
      
      {/* Protected Routes - All authenticated users */}
      <Route element={<ProtectedRoute />}>
        <Route element={<AppShell />}>
          {/* Dashboard - Always accessible */}
          <Route path="/dashboard" element={<Dashboard />} />

          {/* The service-area map. Reachable from the Dashboard panel but
              not from the rail: it was the whole of the old dashboard and
              is not what an AO opens the product for. Kept because it
              works and will matter again when routing lands. */}
          <Route path="/coverage" element={<CoverageMap />} />
          
          {/* Settings - Always accessible */}
          <Route path="/settings" element={<Settings />} />
          {/* Access settings: the page asks the server, which decides who may read it. */}
          <Route path="/settings/access" element={<AccessSettings />} />

          {/* Bug reports. Built months ago and never given a route, so the
              only way to read what testers had filed was to not have one.
              SA-only, and reached from the Settings panel. */}
          <Route element={<ProtectedRoute requiredPermission="companies.view" />}>
            <Route path="/settings/bug-reports" element={<BugReports />} />
          </Route>

          {/* Workflow monitoring — company-scoped by the backend
              (BCs see their own service area) */}
          <Route element={<ProtectedRoute requiredArea="operations" />}>
            <Route path="/operations" element={<Operations />} />
            <Route path="/operations/training" element={<Navigate to="/operations" replace />} />
            <Route path="/operations/:instanceId" element={<OperationDetail />} />
          </Route>
          
          {/* Files & Compliance — the document vault.

              Gated on 'reports.view' rather than a files-specific
              permission the backend does not know about: it maps to
              exactly the right set (SA/CA/AO wildcard, BC explicit,
              Driver/Maintainer excluded), and the real decision is made
              per document type and per row inside the API anyway. A route
              gate here is convenience, not the thing protecting the data. */}
          <Route element={<ProtectedRoute requiredArea="files" />}>
            {/* The top of the Files tree is the folder page too, so going in
                and out of folders is one screen changing its folder. */}
            <Route path="/files" element={<FolderPage />} />
            <Route path="/files/compliance" element={<ComplianceBoardPage />} />
            <Route path="/files/roster/:ownerType" element={<RosterPage />} />
            <Route path="/files/entity/:ownerType/:ownerId" element={<EntityFilePage />} />
            <Route path="/files/category/:category" element={<DocumentListPage mode="category" />} />
            <Route path="/files/search" element={<DocumentListPage mode="search" />} />
            <Route path="/files/document/:documentId" element={<DocumentDetailPage />} />
            <Route path="/files/folder/:folderKey" element={<FolderPage />} />
            <Route path="/files/recently-deleted" element={<RecentlyDeletedPage />} />

            {/* The fleet is the asset record, gated with the vault rather
                than with user management: a BC runs the trucks in their
                area and needs the list. Writing to it is enforced by the
                API (AO/CA to retire), not by this route. */}
          </Route>
          <Route element={<ProtectedRoute requiredArea="fleet" />}>
            <Route path="/fleet" element={<FleetList />} />
            <Route path="/fleet/:vehicleId" element={<VehicleRecord />} />
          </Route>

          {/* Tasks - Check permission dynamically */}
          <Route 
            path="/tasks" 
            element={
              <ProtectedRoute requiredArea="tasks">
                <TaskList />
              </ProtectedRoute>
            } 
          />
          
          {/* Users Management - Check permission */}
          <Route 
            path="/users" 
            element={
              <ProtectedRoute requiredArea="people">
                <UserList />
              </ProtectedRoute>
            } 
          />
          <Route
            path="/users/:accountId"
            element={
              <ProtectedRoute requiredArea="people">
                <UserRecord />
              </ProtectedRoute>
            }
          />
          
          {/* Hiring — the pipeline that produces the people in Users.

              Gated like the vault rather than on 'users.view': a BC runs
              hiring in their area and is the responsible role on most of
              the checkpoint catalog, but does not hold 'users.view'. The
              API router refuses Drivers and Maintainers outright, and a
              BC is scoped to their own service area inside the service,
              exactly as with vehicles. */}
          <Route element={<ProtectedRoute requiredArea="hiring" />}>
            {/* The board is gone. It answered "where is everybody", which
                stopped being a question worth a screen once the to-do list
                answered "what do I do" — and with 34 of 49 sitting in
                Ready it was one tall column and four thin ones. /hiring
                opens on the work. */}
            <Route path="/hiring" element={<CandidateList mode="all" />} />
            <Route path="/hiring/list" element={<CandidateList mode="all" />} />
            <Route path="/hiring/follow-up" element={<CandidateList mode="follow-up" />} />
            <Route path="/hiring/closed" element={<CandidateList mode="closed" />} />
            {/* Moved to Reports; old links keep working. */}
            <Route path="/hiring/insights" element={<Navigate to="/reports/hiring" replace />} />
            <Route path="/reports/hiring" element={<HiringInsights />} />
            {/* Last, so /hiring/list is not read as a candidate id. */}
            <Route path="/hiring/:candidateId" element={<CandidateDetail />} />
          </Route>

          {/* The work schedule against the expected volume. */}
          <Route element={<ProtectedRoute requiredArea="operations" />}>
            <Route path="/schedule" element={<ScheduleWeekPage />} />
            <Route path="/schedule/timeline" element={<ScheduleTimeline />} />
            <Route path="/schedule/usual" element={<UsualWeek />} />
            <Route path="/schedule/work-areas" element={<WorkAreas />} />
            <Route path="/schedule/training" element={<Navigate to="/operations" replace />} />
          </Route>
          {/* Payroll: upload the week's statement, see what to pay. AO/CA
              only, and the API router refuses everyone else regardless. */}
          <Route element={<ProtectedRoute requiredArea="payroll" />}>
            <Route path="/payroll" element={<PayrollWeek />} />
            <Route path="/payroll/weeks/:runId" element={<PayrollWeek />} />
            <Route path="/payroll/history" element={<PayrollHistory />} />
            {/* One route, so picking a person does not remount and reload the page. */}
            <Route path="/payroll/people/:accountId?" element={<DriverPay />} />
            {/* Moved to Reports; old links keep working. */}
            <Route path="/payroll/reports" element={<Navigate to="/reports/payroll" replace />} />
            <Route path="/payroll/volume" element={<Navigate to="/reports/volume" replace />} />
          </Route>
          {/* Reports: the money ones are the `reports` area — a company can
              show someone the numbers without letting them run payroll. */}
          <Route element={<ProtectedRoute requiredArea={['reports', 'hiring']} />}>
            <Route path="/reports" element={<ReportsIndex />} />
          </Route>
          <Route element={<ProtectedRoute requiredArea="reports" />}>
            <Route path="/reports/payroll" element={<PayrollReports />} />
            <Route path="/reports/volume" element={<WeeklyVolume />} />
          </Route>

          {/* Company Management - Check permission */}
          <Route 
            path="/companies" 
            element={
              <ProtectedRoute requiredPermission="companies.view">
                <CompanyList />
              </ProtectedRoute>
            } 
          />
        </Route>
      </Route>
      
      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

const AppWrapper: React.FC = () => (
  <Router>
    <App />
  </Router>
);

export default AppWrapper;
